import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ACCESS_TOKEN } from "@/constants";
import {
  ArrowRight,
  Users,
  TrendingUp,
  Database,
  Plus,
  Activity,
  Edit,
  Trash2,
  Clock,
  User,
  Building2,
  AlertCircle,
  HelpCircle,
  X,
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL;

function BentoCard({
  title,
  value,
  icon: Icon,
  trend,
  className = "",
  iconClassName = "",
}) {
  return (
    <div
      className={`bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-gray-600 text-sm mb-2">{title}</p>
          <p className="text-2xl sm:text-3xl mb-1">{value}</p>
          {trend && (
            <p className="text-sm text-green-600 flex items-center gap-1">
              <TrendingUp size={14} />
              {trend}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${iconClassName}`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
    </div>
  );
}

function formatRelativeTime(isoString) {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString();
}

function getActionText(action) {
  switch (action) {
    case "add":
      return "Added";
    case "edit":
      return "Edited";
    case "delete":
      return "Deleted";
    default:
      return "Updated";
  }
}

function getActionColor(action) {
  switch (action) {
    case "add":
      return "border-emerald-500 text-emerald-500";
    case "edit":
      return "border-sky-500 text-sky-500";
    case "delete":
      return "border-rose-500 text-rose-500";
    default:
      return "border-gray-400 text-gray-500";
  }
}

function getActionIcon(action) {
  const commonProps = { className: "h-4 w-4" };
  switch (action) {
    case "add":
      return <Plus {...commonProps} />;
    case "edit":
      return <Edit {...commonProps} />;
    case "delete":
      return <Trash2 {...commonProps} />;
    default:
      return <AlertCircle {...commonProps} />;
  }
}

function ActivityItem({ activity }) {
  if (!activity) return null;

  const { ownerName, action, accommodationTitle, accommodationId, timestamp } =
    activity;

  const label = getActionText(action);
  const colorClasses = getActionColor(action);

  return (
    <div className="relative pl-6 sm:pl-8 pb-4 border-l-2 border-gray-200 last:border-l-0 last:pb-0">
      <div
        className={`absolute left-[-9px] top-0 h-4 w-4 rounded-full border-2 ${colorClasses} bg-white`}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`h-2 w-2 rounded-full ${colorClasses}`}></div>
        </div>
      </div>

      <div className="bg-white p-3 sm:p-4 rounded-lg">
        <div className="flex items-center gap-2">
          <div
            className={`h-10 w-10 rounded-lg border flex items-center justify-center flex-shrink-0 ${colorClasses}`}>
            {getActionIcon(action)}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 break-words">
                  <span className="font-medium">{ownerName}</span>{" "}
                  <span className="text-gray-600">
                    {getActionText(action).toLowerCase()}
                  </span>{" "}
                  {action !== "delete" && (
                    <span className="text-gray-600">accommodation</span>
                  )}{" "}
                  <span className="font-medium text-[#D2138C]">
                    {accommodationTitle}
                  </span>
                </p>
              </div>

              <Badge
                variant="outline"
                className={`${colorClasses} text-xs whitespace-nowrap self-start sm:self-auto`}>
                {label}
              </Badge>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{formatRelativeTime(timestamp)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                <span>ID: #{accommodationId ?? "—"}</span>
              </div>
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>{ownerName}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Dashboard({ onGoToSdaLog, onGoToUsers, onOpenSidebar }) {
  const [openGuide, setOpenGuide] = useState(false);

  const getToken = () =>
    localStorage.getItem(ACCESS_TOKEN) || sessionStorage.getItem(ACCESS_TOKEN);

  const fetchJSON = async (url) => {
    const token = getToken();

    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const res = await fetch(url, {
      method: "GET",
      headers,
    });

    const text = await res.text().catch(() => "");
    if (!res.ok) {
      throw new Error(
        `Request failed (${res.status}): ${text || res.statusText}`,
      );
    }

    try {
      return text ? JSON.parse(text) : null;
    } catch {
      return null;
    }
  };

  const token = getToken();
  if (!token) {
    throw new Error("Not authenticated. Please login again.");
  }

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: async () => {
      const [activitiesRes, usersRes, uptimeRes, dbRes, profileRes] =
        await Promise.all([
          fetchJSON(`${API_URL}/api/sdaowner/activities`),
          fetchJSON(`${API_URL}/api/admin/get_users`),
          fetchJSON(`${API_URL}/api/admin/system_uptime`),
          fetchJSON(`${API_URL}/api/admin/database_stats`),
          fetchJSON(`${API_URL}/api/admin/profile`),
        ]);

      // activities
      const rawActivities = Array.isArray(activitiesRes)
        ? activitiesRes
        : (activitiesRes && activitiesRes.activities) || [];

      const sortedActivities = rawActivities
        .slice()
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );

      const limitedActivities = sortedActivities.slice(0, 3).map((item) => ({
        ...item,
        ownerName:
          item.ownerName || item.actor || item.user_name || "Unknown user",
      }));

      // users
      const usersList =
        (usersRes && usersRes.users) ||
        (Array.isArray(usersRes) ? usersRes : []);

      // uptime
      const uptimePct =
        uptimeRes && typeof uptimeRes.uptime_percent === "number"
          ? uptimeRes.uptime_percent.toFixed(1) + "%"
          : "0%";

      // database size
      let dbDisplay = "—";
      if (dbRes && dbRes.database_size) {
        dbDisplay = dbRes.database_size;
      } else if (dbRes && typeof dbRes.database_size_bytes === "number") {
        const mb = dbRes.database_size_bytes / (1024 * 1024);
        dbDisplay = `${mb.toFixed(1)} MB`;
      }

      // username
      const profileName =
        (profileRes &&
          (profileRes.full_name || profileRes.name || profileRes.username)) ||
        "";

      return {
        activities: limitedActivities,
        users: usersList,
        systemUptime: uptimePct,
        databaseSize: dbDisplay,
        username: profileName,
      };
    },
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: (prev) => prev,
  });

  const users = (data && data.users) || [];
  const activities = (data && data.activities) || [];
  const systemUptime = (data && data.systemUptime) || "—";
  const databaseSize = (data && data.databaseSize) || "—";
  const username = (data && data.username) || "";

  const activitiesLoading = isLoading;
  const activitiesError = error
    ? error.message || "Error loading dashboard"
    : "";

  const limitedUsers = users.slice(0, 3);
  const limitedSdaOwners = users
    .filter((u) => u.role === "SDA Owner")
    .slice(0, 3);

  const totalUsers = users.length;
  const totalSdaOwners = users.filter((u) => u.role === "SDA Owner").length;
  const totalProperties = 0;

  const NEW_USER_WINDOW_DAYS = 30;
  const now = new Date();

  const newUsersCount = users.filter((u) => {
    const created =
      u.created_at || u.createdAt || u.registered_at || u.created_on;

    if (!created) return false;

    const d = new Date(created);
    if (Number.isNaN(d.getTime())) return false;

    const diffDays = (now - d) / (1000 * 60 * 60 * 24);
    return diffDays <= NEW_USER_WINDOW_DAYS;
  }).length;

  const kpis = [
    { label: "Total Users", value: totalUsers },
    { label: "Total SDA Owners", value: totalSdaOwners },
    { label: "Total Properties", value: totalProperties },
  ];

  return (
    <div className="w-full px-3 sm:px-6 py-4 pt-4 md:pt-4">
      <div className="w-full flex flex-col gap-2 pb-1 md:pb-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center justify-between gap-2 w-full max-w-full md:inline-flex md:w-auto md:justify-start">
          <h1 className="m-0 text-xl sm:text-3xl md:text-4xl font-semibold text-black leading-tight break-words text-left flex-1 min-w-0">
            {username ? `Hello there, ${username}!` : "Hello there!"}
          </h1>

          <button
            type="button"
            onClick={() => setOpenGuide(true)}
            className="h-9 w-9 md:h-9 md:w-9 rounded-full border border-gray-200 hover:bg-gray-100 flex items-center justify-center shrink-0"
            aria-label="Open dashboard guide">
            <HelpCircle className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* KPIs */}
        <div className="w-full md:w-auto">
          {/* Mobile: 2-col grid */}
          <div className="grid grid-cols-2 gap-3 md:hidden">
            {kpis.map((k, idx) => {
              const isLast = idx === kpis.length - 1;
              const isOddCount = kpis.length % 2 === 1;
              const spanLast = isLast && isOddCount;

              return (
                <div
                  key={k.label}
                  className={`rounded-xl border border-gray-200 bg-white px-3 py-3 text-center ${
                    spanLast ? "col-span-2" : ""
                  }`}>
                  <div className="text-[11px] font-medium text-muted-foreground">
                    {k.label}
                  </div>
                  <div className="mt-1 text-xl font-semibold text-gray-900">
                    {k.value}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: keep original look */}
          <div className="hidden md:flex items-center gap-6">
            {kpis.map((k) => (
              <Card
                key={k.label}
                className="min-w-[140px] bg-transparent shadow-none border-0">
                <CardContent className="p-0">
                  <div className="flex flex-col items-center justify-center px-4 py-2">
                    <div className="text-xs font-medium text-muted-foreground">
                      {k.label}
                    </div>
                    <div className="text-2xl lg:text-3xl font-semibold">
                      {k.value}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* MOBILE ONLY: System Uptime, New User Registered, Database Size */}
      <div className="md:hidden pb-2">
        <div className="grid grid-cols-1 gap-3">
          <BentoCard
            title="System Uptime"
            value={systemUptime}
            icon={Activity}
            iconClassName="bg-purple-500"
          />

          <BentoCard
            title="New User Registered"
            value={newUsersCount}
            icon={Users}
            iconClassName="bg-orange-500"
          />

          <BentoCard
            title="Database Size"
            value={databaseSize}
            icon={Database}
            iconClassName="bg-indigo-500"
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
        <div className="w-full lg:w-2/3 space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <CardTitle className="text-sm">Recent Activity</CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full w-full sm:w-auto justify-center"
                onClick={() => onGoToSdaLog && onGoToSdaLog()}>
                <ArrowRight className="mr-2 h-4 w-4" /> View all
              </Button>
            </CardHeader>

            <CardContent>
              {activitiesLoading ? (
                <div className="text-center text-sm text-muted-foreground">
                  Loading activities
                </div>
              ) : activitiesError ? (
                <div className="text-center text-sm text-red-500">
                  {activitiesError}
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground">
                  no recent activity
                </div>
              ) : (
                <div className="space-y-1">
                  {activities.map((activity) => (
                    <ActivityItem key={activity.id} activity={activity} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <CardTitle className="text-sm">Users</CardTitle>

              <Button
                variant="outline"
                size="sm"
                className="rounded-full w-full sm:w-auto justify-center"
                onClick={() => onGoToUsers && onGoToUsers()}>
                <ArrowRight className="mr-2 h-4 w-4" /> View all
              </Button>
            </CardHeader>

            <CardContent className="space-y-3">
              {/* MOBILE: stacked list */}
              <div className="md:hidden">
                {isLoading ? (
                  <div className="text-center text-sm text-muted-foreground py-6">
                    Loading users
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-6">
                    no users found
                  </div>
                ) : (
                  <div className="space-y-2">
                    {limitedUsers.map((user) => (
                      <div
                        key={user.id || user.user_id || user.username}
                        className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 leading-snug break-words">
                              {user.name || user.full_name || user.username}
                            </p>
                            <p className="text-xs text-gray-500 mt-1 break-words">
                              @{user.username || "—"}
                            </p>
                          </div>

                          <Badge
                            variant="secondary"
                            className="whitespace-nowrap">
                            {user.role || "—"}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* DESKTOP: keep table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground">
                          Loading users
                        </TableCell>
                      </TableRow>
                    ) : users.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground">
                          no users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      limitedUsers.map((user) => (
                        <TableRow
                          key={user.id || user.user_id || user.username}
                          className="text-gray-700">
                          <TableCell className="text-left sm:text-center font-medium">
                            {user.name || user.full_name || user.username}
                          </TableCell>
                          <TableCell className="text-center">
                            {user.role}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="w-full lg:w-1/3 space-y-6">
          <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
            <BentoCard
              title="System Uptime"
              value={systemUptime}
              icon={Activity}
              iconClassName="bg-purple-500"
            />

            <BentoCard
              title="New User Registered"
              value={newUsersCount}
              icon={Users}
              iconClassName="bg-orange-500"
            />

            <BentoCard
              title="Database Size"
              value={databaseSize}
              icon={Database}
              iconClassName="bg-indigo-500"
              className="sm:col-span-2 lg:col-span-1"
            />
          </div>

          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <CardTitle className="text-sm">SDA Owner</CardTitle>

              <Button
                variant="outline"
                size="sm"
                className="rounded-full w-full sm:w-auto justify-center"
                onClick={() => onGoToUsers && onGoToUsers()}>
                <ArrowRight className="mr-2 h-4 w-4" /> View all
              </Button>
            </CardHeader>

            <CardContent className="space-y-3">
              {/* MOBILE: stacked list */}
              <div className="md:hidden">
                {isLoading ? (
                  <div className="text-center text-sm text-muted-foreground py-6">
                    Loading sda owners
                  </div>
                ) : limitedSdaOwners.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-6">
                    no sda owners found
                  </div>
                ) : (
                  <div className="space-y-2">
                    {limitedSdaOwners.map((sdaOwner) => (
                      <div
                        key={
                          sdaOwner.id || sdaOwner.user_id || sdaOwner.username
                        }
                        className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 leading-snug break-words">
                              {sdaOwner.name ||
                                sdaOwner.full_name ||
                                sdaOwner.username}
                            </p>

                            <div className="mt-2 space-y-1 text-xs text-gray-600">
                              <p className="break-words">
                                {sdaOwner.email || "—"}
                              </p>
                              <p className="break-words">
                                {sdaOwner.phone || "—"}
                              </p>
                            </div>
                          </div>

                          <Badge
                            variant="secondary"
                            className="whitespace-nowrap">
                            SDA Owner
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* DESKTOP: keep table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground">
                          Loading sda owners
                        </TableCell>
                      </TableRow>
                    ) : limitedSdaOwners.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center text-muted-foreground">
                          no sda owners found
                        </TableCell>
                      </TableRow>
                    ) : (
                      limitedSdaOwners.map((sdaOwner) => (
                        <TableRow
                          key={
                            sdaOwner.id || sdaOwner.user_id || sdaOwner.username
                          }
                          className="text-gray-700">
                          <TableCell className="text-left sm:text-center font-medium">
                            {sdaOwner.name ||
                              sdaOwner.full_name ||
                              sdaOwner.username}
                          </TableCell>
                          <TableCell className="text-center">
                            {sdaOwner.email}
                          </TableCell>
                          <TableCell className="text-center">
                            {sdaOwner.phone}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      {openGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpenGuide(false)}
            aria-label="Close guide"
          />

          <div className="relative z-10 w-[95%] max-w-2xl rounded-2xl bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-base font-semibold text-gray-900">
                Dashboard guide
              </h2>

              <button
                type="button"
                onClick={() => setOpenGuide(false)}
                className="h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                aria-label="Close">
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="px-6 py-5 text-sm text-gray-700 space-y-5">
              <div>
                <p className="font-semibold text-gray-900 mb-2">
                  What you can do here
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>View system statistics and health.</li>
                  <li>Monitor recent SDA Owner activity.</li>
                  <li>Check total users, SDA Owners, and properties.</li>
                  <li>Quickly navigate to Users and SDA Logs.</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-900 mb-2">
                  Recent Activity
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Shows the latest SDA Owner actions.</li>
                  <li>Click “View all” to open the full activity log.</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-900 mb-2">
                  System widgets
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>System uptime shows backend stability.</li>
                  <li>New User Registered shows recent signups.</li>
                  <li>Database Size shows current storage usage.</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end border-t px-6 py-4">
              <Button
                type="button"
                className="bg-[#D2138C] hover:bg-[#B01076] text-white rounded-lg"
                onClick={() => setOpenGuide(false)}>
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
